import { useState } from "react";
import { useAuth } from "@/store/AuthStore";
import { useApp } from "@/store/AppStore";
import { useTeam } from "@/hooks/useTeam";
import SectionTitle from "@/components/base/SectionTitle";
import FullLoader from "@/components/base/FullLoader";
import TeamStats from "@/pages/admin/equipo/components/TeamStats";
import TeamTable from "@/pages/admin/equipo/components/TeamTable";
import CreateAccountModal from "@/pages/admin/equipo/components/CreateAccountModal";
import ProfileContactModal, {
  type ContactFields,
} from "@/components/layout/ProfileContactModal";
import ChangePasswordModal from "@/components/layout/ChangePasswordModal";
import { adminSetPassword } from "@/store/api";
import type { CreateAccountPayload } from "@/store/api";
import type { TeamMember } from "@/types";

// Módulo de administración del equipo: crear cuentas y cambiar roles.
const TeamPage = () => {
  const { user } = useAuth();
  const { refresh: refreshApp, updateProfileContact } = useApp();
  const { members, loading, error, refresh, changeRole, create } = useTeam();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<TeamMember | null>(null);
  const [pwdTarget, setPwdTarget] = useState<TeamMember | null>(null);
  const [busyId, setBusyId] = useState<string | undefined>(undefined);
  const [feedback, setFeedback] = useState("");

  const notify = (msg: string) => {
    setFeedback(msg);
    setTimeout(() => setFeedback(""), 2600);
  };

  const handleChangeRole = async (id: string, role: "admin" | "asesor") => {
    setBusyId(id);
    try {
      await changeRole(id, role);
      await refreshApp();
      notify(
        role === "admin"
          ? "Cuenta promovida a administrador."
          : "Se quitó el rol de administrador. La cuenta vuelve a ser asesor."
      );
    } catch (e) {
      notify(e instanceof Error ? e.message : "No se pudo cambiar el rol.");
    } finally {
      setBusyId(undefined);
    }
  };

  const handleCreate = async (payload: CreateAccountPayload) => {
    await create(payload);
    await refreshApp();
    notify(
      payload.role === "admin"
        ? "Cuenta de administrador creada. Ya puede iniciar sesión."
        : "Cuenta de asesor creada. Ya puede iniciar sesión."
    );
  };

  const handleSaveContact = async (fields: ContactFields): Promise<boolean> => {
    if (!editing) return false;
    const ok = await updateProfileContact(editing.id, {
      email: fields.email,
      phone: fields.phone,
      address: fields.address,
    });
    if (ok) {
      await refresh();
      notify("Datos de contacto actualizados.");
    }
    return ok;
  };

  const handleSavePassword = async (pwd: string): Promise<boolean> => {
    if (!pwdTarget) return false;
    try {
      await adminSetPassword(pwdTarget.id, pwd);
      notify(`Contraseña actualizada para ${pwdTarget.names}.`);
      return true;
    } catch (e) {
      notify(e instanceof Error ? e.message : "No se pudo cambiar la contraseña.");
      return false;
    }
  };

  if (loading) return <FullLoader label="Cargando el equipo..." />;

  return (
    <div className="pb-24">
      <SectionTitle
        text="Equipo y Administradores"
        subtitle="Crea cuentas nuevas y sube o retira el rol de administrador. Las cuentas de asesor creadas aquí quedan aprobadas y listas para entrar."
      />

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <p className="text-xs text-foreground-600 max-w-2xl">
          El registro público solo crea asesores. Desde aquí conviertes una cuenta de asesor en
          administrador o creas una cuenta de administrador desde cero.
        </p>
        <button
          onClick={() => setCreating(true)}
          className="self-start px-4 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm font-semibold whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
        >
          <i className="ri-user-add-line" /> Crear cuenta
        </button>
      </div>

      <TeamStats members={members} />

      {error && (
        <div className="mt-4 rounded-lg glass-panel border-primary-400/40 px-4 py-3 flex items-center justify-between gap-3">
          <p className="text-xs text-foreground-800 flex items-center gap-2">
            <i className="ri-error-warning-line text-primary-200" /> {error}
          </p>
          <button
            onClick={() => void refresh()}
            className="px-3 py-1.5 rounded-md border border-accent-500/35 text-accent-200 hover:bg-accent-500/10 text-xs font-semibold whitespace-nowrap cursor-pointer"
          >
            Reintentar
          </button>
        </div>
      )}

      {feedback && (
        <div className="mt-4 rounded-md glass-panel border-accent-500/30 px-4 py-2.5 flex items-center gap-2 animate-fade-up">
          <i className="ri-checkbox-circle-line text-accent-300" />
          <p className="text-xs text-foreground-800">{feedback}</p>
        </div>
      )}

      <div className="mt-5">
        <TeamTable
          members={members}
          currentUserId={user?.id}
          onChangeRole={handleChangeRole}
          onEditContact={(m) => setEditing(m)}
          onEditPassword={(m) => setPwdTarget(m)}
          busyId={busyId}
        />
      </div>

      <CreateAccountModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreate={handleCreate}
      />

      <ProfileContactModal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title="Editar contacto"
        subtitle="Correo, teléfono y dirección de la cuenta."
        displayName={editing ? `${editing.names} ${editing.surnames}`.trim() : ""}
        initial={{
          email: editing?.email ?? "",
          phone: editing?.phone ?? "",
          address: editing?.address ?? "",
        }}
        showAddress
        onSave={handleSaveContact}
      />

      <ChangePasswordModal
        open={pwdTarget !== null}
        onClose={() => setPwdTarget(null)}
        title="Cambiar contraseña"
        subtitle="Asigna una contraseña nueva a esta cuenta."
        displayName={pwdTarget ? `${pwdTarget.names} ${pwdTarget.surnames}`.trim() : ""}
        onSave={handleSavePassword}
      />
    </div>
  );
};

export default TeamPage;